import { useState, useCallback } from 'react';
import { fetchGraphql } from '../graphql/fetch';

export type SubmissionStatus = 'idle' | 'uploading' | 'submitting' | 'success' | 'error';

export interface SubmissionPayload {
  id: number;
  description?: string | null;
}

export interface SubmitOptions {
  files: File[];
  description?: string;
  submissionParams?: Record<string, unknown>;
  /**
   * @deprecated Use `submissionParams` instead. Kept only so admin-authored
   * components written before `submissionParams` existed (e.g. the live
   * arc-white-box-estimation-challenge-2026 submission form) keep working.
   * Forwarded as-is to the `meta` GraphQL argument, which the server
   * sanitises before it ever reaches submissions.meta — see
   * Submissions::SanitizeLegacyMetaService. New components should use
   * `submissionParams`, which is strictly validated instead of filtered.
   */
  meta?: Record<string, unknown>;
}

export class SubmissionError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'SubmissionError';
  }
}

export class QuotaExceededError    extends SubmissionError { constructor(m: string) { super('QUOTA_EXCEEDED', m); } }
export class RoundLockedError      extends SubmissionError { constructor(m: string) { super('ROUND_LOCKED', m); } }
export class RestrictedIpError     extends SubmissionError { constructor(m: string) { super('RESTRICTED_IP', m); } }
export class RulesNotAcceptedError extends SubmissionError { constructor(m: string) { super('RULES_NOT_ACCEPTED', m); } }

function typedFromCode(code: string, message: string): SubmissionError {
  switch (code) {
    case 'QUOTA_EXCEEDED':     return new QuotaExceededError(message);
    case 'ROUND_LOCKED':       return new RoundLockedError(message);
    case 'RESTRICTED_IP':      return new RestrictedIpError(message);
    case 'RULES_NOT_ACCEPTED': return new RulesNotAcceptedError(message);
    default:                   return new SubmissionError(code || 'UNKNOWN', message);
  }
}

export function useSubmission({ challengeSlug }: { challengeSlug: string }) {
  const [status, setStatus]    = useState<SubmissionStatus>('idle');
  const [error, setError]      = useState<SubmissionError | null>(null);
  const [submission, setSubmission] = useState<SubmissionPayload | null>(null);

  const submit = useCallback(async ({ files, description, submissionParams, meta }: SubmitOptions) => {
    setStatus('uploading');
    setError(null);
    setSubmission(null);

    try {
      const fileInputs: Array<{ s3Key: string; submissionType: string }> = [];

      for (const file of files) {
        const presignRes = await fetchGraphql<{ requestSubmissionUpload: { url: string | null; fields: Record<string, string> | null; key: string; errors: Array<{ message: string; code?: string }> } }>({
          query: `mutation($input: RequestSubmissionUploadInput!) {
            requestSubmissionUpload(input: $input) {
              url fields key errors { message code }
            }
          }`,
          variables: { input: { challengeSlug, filename: file.name, contentType: file.type || 'application/octet-stream' } }
        });
        const presignPayload = presignRes.data && presignRes.data.requestSubmissionUpload;
        if (!presignPayload) throw new SubmissionError('UNKNOWN', (presignRes.errors && presignRes.errors[0] && presignRes.errors[0].message) || 'Presign failed');
        if (presignPayload.errors && presignPayload.errors.length) {
          throw typedFromCode(presignPayload.errors[0].code || 'UNKNOWN', presignPayload.errors[0].message);
        }

        // Skip the actual upload in dry-run mode (server returns null url)
        if (presignPayload.url && presignPayload.fields) {
          const form = new FormData();
          Object.entries(presignPayload.fields).forEach(([k, v]) => form.append(k, v));
          form.append('file', file);
          const s3Res = await fetch(presignPayload.url, { method: 'POST', body: form });
          if (!s3Res.ok) throw new SubmissionError('S3_UPLOAD_FAILED', `S3 upload failed: ${s3Res.status}`);
        }

        fileInputs.push({ s3Key: presignPayload.key, submissionType: 'artifact' });
      }

      setStatus('submitting');
      const createRes = await fetchGraphql<{ createSubmission: { submission: SubmissionPayload | null; errors: Array<{ message: string; code?: string }> } }>({
        query: `mutation($input: CreateSubmissionInput!) {
          createSubmission(input: $input) {
            submission { id description }
            errors { message code }
          }
        }`,
        variables: { input: { challengeSlug, files: fileInputs, description, submissionParams, meta } }
      });
      const payload = createRes.data && createRes.data.createSubmission;
      if (!payload) throw new SubmissionError('UNKNOWN', (createRes.errors && createRes.errors[0] && createRes.errors[0].message) || 'createSubmission failed');
      if (payload.errors && payload.errors.length) {
        throw typedFromCode(payload.errors[0].code || 'UNKNOWN', payload.errors[0].message);
      }

      setSubmission(payload.submission);
      setStatus('success');
      return { success: true, submission: payload.submission };
    } catch (err) {
      const tErr = err instanceof SubmissionError ? err : new SubmissionError('UNKNOWN', String(err));
      setError(tErr);
      setStatus('error');
      throw tErr;
    }
  }, [challengeSlug]);

  return { submit, status, error, submission };
}
